/**
 * Fil d'Ariane (UX-03, A11Y-05 ; § 4.9 ; AC-N3) : `Groupe › Entrée › Objet`, 3 segments au plus,
 * et JAMAIS le sélecteur de contexte (il ne répète ni l'organisation, ni le programme, ni le
 * projet). Bureau : 3 segments ; tablette : `Entrée › Objet` ; téléphone : un lien retour
 * « ‹ Entrée » quand un objet est ouvert, sinon le titre de l'écran.
 *
 * Landmark `nav` « Fil d'Ariane », liste `ol`, `aria-current="location"` sur le dernier segment.
 */
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { useT } from '@/i18n/language'
import { segmentsFilAriane, type SegmentFil } from '@/lib/nav'

/** `partie` (U6b vague 9) : la coquille rend le fil large dans la barre haute et la partie
 *  téléphone EN TÊTE DU CONTENU (planche CoquilleTelephone : « ‹ Tests » dans main). */
export function FilAriane({ objet, partie = 'tout' }: { objet?: string; partie?: 'tout' | 'telephone' | 'large' }): JSX.Element {
  const { t } = useT()
  const { pathname, search } = useLocation()
  const segments = segmentsFilAriane(pathname, search, objet)
  const dernier = segments.length - 1
  const entree = [...segments].reverse().find((s) => s.genre === 'entree')
  const objetOuvert = segments[dernier]?.genre === 'objet'
  const libelle = (s: SegmentFil): string => s.genre === 'objet' ? (s.cle ? t(s.cle, s.label) : s.label)
    : s.genre === 'groupe' ? t(`grp.${s.label}`, s.label) : t(`nav.${s.to ?? ''}`, s.label)

  return (
    <>
      {/* Téléphone : retour vers l'entrée quand un objet est ouvert ; sinon le titre. */}
      {partie !== 'large' ? (
      <div className="min-w-0 flex-1 md:hidden" data-testid="fil-telephone">
        {objetOuvert && entree?.to ? (
          <Link to={entree.to} className="inline-flex min-h-11 max-w-full items-center gap-1 truncate text-sm font-medium">
            <ChevronLeft className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{libelle(entree)}</span>
          </Link>
        ) : (
          <h2 className="truncate text-sm font-semibold">{segments[dernier] ? libelle(segments[dernier]!) : 'DoIt'}</h2>
        )}
      </div>
      ) : null}
      {partie !== 'telephone' ? (
      <nav aria-label={t('fil.region', 'Fil d’Ariane')} data-testid="fil-ariane" className="hidden min-w-0 flex-1 md:block">
        <ol className="flex min-w-0 flex-nowrap items-center gap-1.5 text-[0.8125rem]">
          {segments.map((s, i) => {
            const courant = i === dernier
            // Tablette : `Entrée › Objet` — le segment de groupe ne s'affiche qu'au bureau.
            const masqueTablette = s.genre === 'groupe' ? 'hidden lg:flex' : 'flex'
            return (
                <li key={`${s.genre}-${s.label}`} className={`${masqueTablette} min-w-0 items-center gap-1.5`} data-testid={`fil-${s.genre}`}>
                  {i > 0 ? <ChevronRight className={`size-3.5 shrink-0 text-muted-foreground ${s.genre === 'entree' && segments[i - 1]?.genre === 'groupe' ? 'hidden lg:block' : ''}`} aria-hidden="true" /> : null}
                  {courant ? (
                    <span aria-current="location" className="max-w-64 truncate font-semibold">{libelle(s)}</span>
                  ) : s.to ? (
                    <Link to={s.to} className="inline-flex min-h-6 max-w-48 items-center truncate text-brand-text underline underline-offset-[3px] hover:no-underline">{libelle(s)}</Link>
                  ) : (
                    <span className="max-w-48 truncate text-muted-foreground">{libelle(s)}</span>
                  )}
                </li>
            )
          })}
        </ol>
      </nav>
      ) : null}
    </>
  )
}
